import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useRole } from "../lib/roleContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { StatusBadge } from "../components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { ChevronLeft, ShieldCheck, ShieldAlert, ShieldX, HeartPulse, Stethoscope, Wrench, UserMinus, UserCog, Ban, FileCheck2, CalendarOff, Flag, Trophy, Radio, Users, Users2 } from "lucide-react";

const KIND_META = {
  junior: { label: "Junior session", Icon: Trophy },
  members: { label: "Members evening", Icon: Users },
  briefing: { label: "Paddock briefing", Icon: Radio },
  marshals: { label: "Trackside rota", Icon: Flag },
  mixed: { label: "Mixed session", Icon: Users2 },
};

const DBS_BADGE = {
  current: { status: "green", label: "DBS current" },
  expired: { status: "amber", label: "DBS expired" },
  missing: { status: "red", label: "DBS missing" },
};
const SAFE_BADGE = {
  current: { status: "green", label: "Safeguarding current" },
  expired: { status: "red", label: "Safeguarding expired" },
  not_required: { status: "neutral", label: "Not required" },
};

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function EventDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { role } = useRole();
  const [event, setEvent] = useState(null);
  const [decision, setDecision] = useState(null); // {exception, action}
  const [notes, setNotes] = useState("");
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveNotes, setApproveNotes] = useState("");

  const refresh = async () => setEvent(await api.getEvent(id));
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  if (!event) return <Card className="p-8 text-sm text-muted-foreground">Loading…</Card>;

  const r = event.readiness;
  const openExcs = event.exceptions.filter((x) => x.status === "open");
  const resolvedExcs = event.exceptions.filter((x) => x.status !== "open");

  const submitDecision = async (newStatus, action, label) => {
    if (!decision) return;
    await api.recordDecision({
      target_type: "exception",
      target_id: decision.exception.id,
      reviewer_role: role.label,
      reviewer_name: role.name,
      action,
      notes,
      new_status: newStatus,
    });
    toast.success(`${label} recorded by ${role.label}`);
    setDecision(null); setNotes("");
    refresh();
  };

  const approve = async () => {
    try {
      await api.approveEvent(event.id, { reviewer_name: role.name, reviewer_role: role.label, notes: approveNotes });
      toast.success("Session readiness approved");
      setApproveOpen(false); setApproveNotes("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to approve");
    }
  };

  const postpone = async () => {
    await api.recordDecision({
      target_type: "event",
      target_id: event.id,
      reviewer_role: role.label,
      reviewer_name: role.name,
      action: "postpone_session",
      notes: "Postponed pending unresolved exceptions",
      event_approval: "postpone",
    });
    toast.message("Session postponed", { description: "Decision recorded in evidence trail." });
    refresh();
  };

  const generateReceipt = async () => {
    const r = await api.createReceipt({ event_id: event.id });
    nav(`/receipts/${r.id}`);
  };

  return (
    <div className="space-y-8" data-testid="event-detail-page">
      <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-dashboard">
        <ChevronLeft className="h-4 w-4" /> Back to dashboard
      </Link>

      <header className="grid lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 space-y-2">
          <div className="text-[11px] tracking-eyebrow text-muted-foreground">
            {(KIND_META[event.kind] || KIND_META.mixed).label} · {event.location}
          </div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-medium">{event.title}</h1>
          <div className="font-mono-ui text-xs text-muted-foreground">
            {fmt(event.start_at)} → {fmt(event.end_at)} · Supervision ratio: {event.min_supervision_ratio}
          </div>
        </div>
        <Card className="p-5 border-border space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-[11px] tracking-eyebrow text-muted-foreground">Readiness</div>
            <StatusBadge status={r.overall} size="lg" testId="event-overall-rag" />
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {Object.entries(r.sub_scores).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between border-b border-dashed border-border py-1">
                <span className="capitalize text-muted-foreground">{k.replace("_", " ")}</span>
                <span className="font-mono-ui font-medium" style={{
                  color: v === "green" ? "#4F7159" : v === "amber" ? "#B27C24" : "#C25953"
                }}>{v.toUpperCase()}</span>
              </div>
            ))}
          </div>
          <div className="text-xs text-muted-foreground">
            {event.approval_status === "approved"
              ? <>Approved by <span className="font-medium text-foreground">{event.approved_by}</span> · <span className="font-mono-ui">{fmt(event.approved_at)}</span></>
              : event.approval_status === "postponed"
                ? "Session postponed."
                : "Awaiting Club Secretary approval."}
          </div>
        </Card>
      </header>

      <Tabs defaultValue="people" className="space-y-4">
        <TabsList data-testid="event-tabs">
          <TabsTrigger value="people" data-testid="tab-people">Coaches &amp; volunteers</TabsTrigger>
          <TabsTrigger value="exceptions" data-testid="tab-exceptions">Open exceptions ({openExcs.length})</TabsTrigger>
          <TabsTrigger value="trail" data-testid="tab-trail">Evidence trail</TabsTrigger>
        </TabsList>

        <TabsContent value="people" className="space-y-3">
          <Card className="border-border">
            <div className="divide-y divide-border">
              {event.assignments_full.map((a) => {
                const p = a.person;
                const dbs = DBS_BADGE[p.dbs_status] || { status: "neutral", label: p.dbs_status };
                const safe = SAFE_BADGE[p.safeguarding_training] || { status: "neutral", label: p.safeguarding_training };
                return (
                  <div key={p.id} className="flex items-center gap-4 p-4" data-testid={`person-row-${p.id}`}>
                    <img src={p.avatar_url} alt="" className="h-10 w-10 rounded-full object-cover bg-muted" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[15px]">{p.name}</div>
                      <div className="text-xs text-muted-foreground">{a.role} · {p.role_title}</div>
                    </div>
                    <div className="flex flex-wrap gap-2 justify-end">
                      <StatusBadge status={dbs.status} label={dbs.label} testId={`dbs-${p.id}`} />
                      <StatusBadge status={safe.status} label={safe.label} testId={`safe-${p.id}`} />
                      {p.first_aider && (
                        <span className="inline-flex items-center gap-1 text-xs font-mono-ui px-2 py-1 rounded-full" style={{ backgroundColor: "#EDF3EE", color: "#4F7159" }}>
                          <HeartPulse className="h-3 w-3" /> First aider
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="exceptions" className="space-y-3">
          {openExcs.length === 0 && (
            <Card className="p-6 text-sm text-muted-foreground">No open exceptions. Session can be put forward for approval.</Card>
          )}
          {openExcs.map((x) => (
            <ExceptionRow key={x.id} x={x} role={role} onAction={(action) => { setDecision({ exception: x, action }); setNotes(""); }} />
          ))}
          {resolvedExcs.length > 0 && (
            <div className="pt-2">
              <div className="text-[11px] tracking-eyebrow text-muted-foreground mb-2">Resolved / accepted</div>
              <div className="space-y-2">
                {resolvedExcs.map((x) => (
                  <Card key={x.id} className="p-4 border-border flex items-start justify-between gap-4" data-testid={`exception-resolved-${x.id}`}>
                    <div>
                      <div className="text-sm font-medium">{x.title}</div>
                      <div className="text-xs text-muted-foreground mt-1">{x.detail}</div>
                      <div className="text-[11px] font-mono-ui text-muted-foreground mt-1">{x.status.toUpperCase()} by {x.resolved_by} · {fmt(x.resolved_at)}</div>
                    </div>
                    <StatusBadge status={x.status === "resolved" ? "green" : "amber"} label={x.status === "resolved" ? "Resolved" : "Accepted"} />
                  </Card>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="trail">
          <Card className="p-6 border-border">
            {event.decisions.length === 0 ? (
              <div className="text-sm text-muted-foreground">No decisions recorded yet. Each governance action will appear here with a timestamp.</div>
            ) : (
              <ol className="space-y-3 font-mono-ui text-xs">
                {event.decisions.map((d) => (
                  <li key={d.id} className="border-l-2 border-border pl-3" data-testid={`decision-${d.id}`}>
                    <div className="text-muted-foreground">{fmt(d.timestamp)} — {d.id}</div>
                    <div className="text-sm font-sans"><b>{d.reviewer_name}</b> ({d.reviewer_role}) → {d.action}</div>
                    {d.notes && <div className="text-muted-foreground font-sans mt-0.5">“{d.notes}”</div>}
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <section className="space-y-3" data-testid="governance-gate">
        <div className="text-[11px] tracking-eyebrow text-muted-foreground">Governance gate · Acting as {role.label}</div>
        <Card className="p-6 border-border">
          <div className="flex flex-wrap gap-3 items-center">
            <Button
              onClick={() => setApproveOpen(true)}
              disabled={openExcs.length > 0 || event.approval_status === "approved" || role.id !== "club_secretary"}
              data-testid="approve-readiness-btn"
            >
              <FileCheck2 className="h-4 w-4 mr-1.5" /> Approve session readiness
            </Button>
            <Button
              variant="outline"
              onClick={postpone}
              disabled={role.id !== "club_secretary" || event.approval_status === "postponed"}
              data-testid="postpone-session-btn"
            >
              <CalendarOff className="h-4 w-4 mr-1.5" /> Postpone session
            </Button>
            <Button variant="outline" onClick={generateReceipt} data-testid="generate-receipt-btn">
              <ShieldCheck className="h-4 w-4 mr-1.5" /> Generate evidence receipt
            </Button>
            {openExcs.length > 0 && (
              <span className="text-xs text-muted-foreground">
                Approval is blocked while {openExcs.length} exception{openExcs.length === 1 ? " is" : "s are"} open.
              </span>
            )}
            {role.id !== "club_secretary" && openExcs.length === 0 && event.approval_status !== "approved" && (
              <span className="text-xs text-muted-foreground">
                Switch to <b>Club Secretary</b> to approve readiness.
              </span>
            )}
          </div>
        </Card>
      </section>

      {/* Decision dialog */}
      <Dialog open={!!decision} onOpenChange={(o) => !o && setDecision(null)}>
        <DialogContent data-testid="decision-dialog">
          <DialogHeader>
            <DialogTitle>{decision?.action?.label}</DialogTitle>
            <DialogDescription>
              {decision?.exception?.title} — recorded as a decision by <b>{role.name}</b> ({role.label}).
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Add a short note for the evidence trail (optional)…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="decision-notes-input"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDecision(null)} data-testid="decision-cancel">Cancel</Button>
            <Button
              onClick={() => submitDecision(decision.action.new_status, decision.action.id, decision.action.label)}
              data-testid="decision-confirm"
            >
              Record decision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve dialog */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve session readiness</DialogTitle>
            <DialogDescription>
              Approving as <b>{role.name}</b> ({role.label}). This is a duty-of-care decision and will be receipted.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Mitigations and conditions for this approval…"
            value={approveNotes}
            onChange={(e) => setApproveNotes(e.target.value)}
            data-testid="approve-notes-input"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setApproveOpen(false)}>Cancel</Button>
            <Button onClick={approve} data-testid="approve-confirm">Approve &amp; receipt</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ExceptionRow({ x, role, onAction }) {
  const sevColor = x.severity === "high" ? "#C25953" : x.severity === "medium" ? "#B27C24" : "#4F7159";
  const actions = actionsForException(x);
  const canAct = role.label === x.required_role || role.id === "club_secretary";

  return (
    <Card className="p-5 border-border space-y-3" data-testid={`exception-${x.id}`}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5">
          {x.severity === "high" ? <ShieldX className="h-5 w-5" style={{ color: sevColor }} />
            : x.severity === "medium" ? <ShieldAlert className="h-5 w-5" style={{ color: sevColor }} />
            : <ShieldCheck className="h-5 w-5" style={{ color: sevColor }} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-medium leading-snug">{x.title}</div>
          <div className="text-sm text-muted-foreground mt-0.5">{x.detail}</div>
          <div className="text-[11px] tracking-eyebrow text-muted-foreground mt-2">
            Severity: <span className="font-mono-ui" style={{ color: sevColor }}>{x.severity}</span> ·
            Owner: {x.required_role} ·
            Kind: <span className="font-mono-ui">{x.kind}</span>
          </div>
        </div>
        <StatusBadge status="red" label="Open" testId={`exception-status-${x.id}`} />
      </div>
      <div className="flex flex-wrap gap-2 pt-1">
        {actions.map((a) => (
          <Button
            key={a.id}
            variant="outline"
            size="sm"
            disabled={!canAct}
            onClick={() => onAction(a)}
            data-testid={`exception-action-${x.id}-${a.id}`}
          >
            <a.Icon className="h-3.5 w-3.5 mr-1.5" /> {a.label}
          </Button>
        ))}
        {!canAct && (
          <span className="text-xs text-muted-foreground">Switch role to <b>{x.required_role}</b> to act.</span>
        )}
      </div>
    </Card>
  );
}

function actionsForException(x) {
  if (x.kind === "DBS_MISSING") {
    return [
      { id: "replace_volunteer", label: "Replace volunteer", Icon: UserMinus, new_status: "resolved" },
      { id: "supervised_only", label: "Re-role as supervised-only", Icon: UserCog, new_status: "accepted" },
    ];
  }
  if (x.kind === "SAFEGUARDING_EXPIRED") {
    return [
      { id: "supervised_only", label: "Restrict to supervised role", Icon: UserCog, new_status: "accepted" },
      { id: "stand_down", label: "Stand coach down for session", Icon: Ban, new_status: "resolved" },
    ];
  }
  if (x.kind === "PREMISES_RISK" || x.kind === "PADDOCK_ACCESS") {
    return [
      { id: "restrict_access", label: "Restrict access", Icon: Ban, new_status: "accepted" },
      { id: "assign_repair", label: "Assign repair / marshal", Icon: Wrench, new_status: "accepted" },
      { id: "close_with_evidence", label: "Close with evidence", Icon: FileCheck2, new_status: "resolved" },
    ];
  }
  if (x.kind === "FIRST_AID_COVER") {
    return [
      { id: "assign_first_aider", label: "Assign first aider on site", Icon: Stethoscope, new_status: "resolved" },
      { id: "accept_oncall", label: "Accept on-call cover", Icon: FileCheck2, new_status: "accepted" },
    ];
  }
  if (x.kind === "EMERGENCY_CONTACTS") {
    return [
      { id: "chase_parents", label: "Chase parents for contacts", Icon: UserCog, new_status: "accepted" },
      { id: "stand_down_juniors", label: "Stand affected juniors down", Icon: Ban, new_status: "resolved" },
    ];
  }
  if (x.kind === "EQUIPMENT_CHECK") {
    return [
      { id: "rescrutineer", label: "Re-scrutineer equipment", Icon: Wrench, new_status: "resolved" },
      { id: "swap_unit", label: "Swap unit from stores", Icon: UserCog, new_status: "resolved" },
    ];
  }
  return [{ id: "resolve", label: "Mark resolved", Icon: FileCheck2, new_status: "resolved" }];
}
